import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import ContinuousCamera from './ContinuousCamera';
import CameraService, { PhotoData } from '../services/CameraService';
import { Camera } from '@capacitor/camera';

interface PhotoCaptureProps {
  onPhotosCaptured: (photos: PhotoData[]) => void;
  onCaptureComplete?: () => void; // Called after photos are captured and processed
  maxPhotos?: number;
  buttonLabel?: string;
  buttonClass?: string;
  allowUpload?: boolean;
}

const PhotoCapture: React.FC<PhotoCaptureProps> = ({
  onPhotosCaptured,
  onCaptureComplete,
  maxPhotos = 30,
  buttonLabel = 'Take Photos',
  buttonClass = 'bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-2 px-4 rounded transition-colors',
  allowUpload = true
}) => {
  const [isCustomCameraOpen, setIsCustomCameraOpen] = useState(false);
  const [supportsCustomCamera, setSupportsCustomCamera] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);
  
  const cameraService = CameraService.getInstance();
  
  useEffect(() => {
    // Check if custom camera is supported
    setSupportsCustomCamera(cameraService.supportsCustomCamera());
  }, []);
  
  const handleOpenCamera = async () => {
    setIsCapturing(true);
    
    try {
      if (supportsCustomCamera) {
        // Open custom camera
        setIsCustomCameraOpen(true);
      } else {
        // Use standard camera as fallback
        const photos = await cameraService.takeMultiplePhotosStandard(maxPhotos);
        onPhotosCaptured(photos);
      }
    } catch (error) {
      console.error('Error opening camera:', error);
      alert('There was an error opening the camera. Please try again.');
    } finally {
      setIsCapturing(false);
    }
  };
  
  const handleCustomCameraClose = () => {
    setIsCustomCameraOpen(false);
  };
  
  const handleCustomPhotosCaptured = async (photoDataUrls: string[]) => {
    // Process and optimize photos from custom camera
    const photos = await cameraService.processCustomCameraPhotos(photoDataUrls);
    onPhotosCaptured(photos);
    
    // Notify that capture is complete (for triggering autosave)
    if (onCaptureComplete) {
      // Small delay to ensure photo processing completes
      setTimeout(() => onCaptureComplete(), 100);
    }
  };
  
  // Check and request camera permissions
  const checkPermissions = async () => {
    try {
      const permission = await Camera.checkPermissions();
      
      if (permission.camera !== 'granted') {
        const requested = await Camera.requestPermissions();
        return requested.camera === 'granted';
      }
      
      return true;
    } catch (error) {
      console.error('Error checking camera permissions:', error);
      return false;
    }
  };
  
  // Handle button click
  const handleCaptureClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Blur any active input to dismiss the keyboard before opening camera
    if (document.activeElement && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    
    // Small delay to ensure keyboard dismisses before camera opens
    await new Promise(resolve => setTimeout(resolve, 100));
    
    const hasPermission = await checkPermissions();
    
    if (hasPermission) {
      handleOpenCamera();
    } else {
      alert('Camera permission is required to take photos');
    }
  };
  
  // Handle file upload from device with optimization
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setIsCapturing(true);
    
    try {
      const photoPromises = Array.from(files).slice(0, maxPhotos).map(file => {
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (event) => {
            const dataUrl = event.target?.result as string;
            resolve(dataUrl);
          };
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });
      });
      
      const dataUrls = await Promise.all(photoPromises);
      
      // Process and optimize uploaded photos
      const photos = await cameraService.processCustomCameraPhotos(dataUrls);
      onPhotosCaptured(photos);
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('There was an error uploading the files. Please try again.');
    } finally {
      setIsCapturing(false);
      // Reset input
      e.target.value = '';
    }
  };
  
  // Render ContinuousCamera in a portal at the document root level
  const renderCameraPortal = () => {
    if (!supportsCustomCamera || !isCustomCameraOpen) return null;
    
    return createPortal(
      <ContinuousCamera
        isOpen={isCustomCameraOpen}
        onClose={handleCustomCameraClose}
        onPhotosCaptured={handleCustomPhotosCaptured}
        maxPhotos={maxPhotos}
      />,
      document.body // Attach directly to body instead of current component hierarchy
    );
  };
  
  return (
    <div className="flex gap-2">
      <button
        type="button"
        onClick={handleCaptureClick}
        disabled={isCapturing}
        className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isCapturing ? (
          <>
            <svg className="animate-spin h-[18px] w-[18px]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm">Processing...</span>
          </>
        ) : (
          <>
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
              <circle cx="12" cy="13" r="4"></circle>
            </svg>
            <span className="text-sm">Camera</span>
          </>
        )}
      </button>
      
      {allowUpload && (
        <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium rounded-lg shadow-md hover:shadow-lg transition-all duration-200 hover:scale-[1.02] cursor-pointer">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <span className="text-sm">Upload</span>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileUpload}
            className="hidden"
            disabled={isCapturing}
          />
        </label>
      )}
      
      {/* Render camera through portal for true fullscreen */}
      {renderCameraPortal()}
    </div>
  );
};

export default PhotoCapture; 